import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from "react-router-dom";
import fetchWithTenant from '../utils/fetchWithTenant';
import useAuthStore from '../stores/authStore';
import { Phone, Video, Unlock, Bell, X, RefreshCw, Camera, AlertCircle, Settings, Loader2, CheckCircle, Volume2, VolumeX, Mic, MicOff, HelpCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import useStore from '../stores/useStore';
import useIntercomControls from '../hooks/useIntercomControls';
import CcmLicenseBanner from '../components/CcmLicenseBanner';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import intercomsTutorial from '../components/tutorials/configs/intercomsTutorial';

const Intercoms = () => {
  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, dismissTutorial } = useTutorialPrompt(intercomsTutorial);

  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantVersion } = useAuthStore();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [intercomSnapshots, setIntercomSnapshots] = useState({});
  const [intercoms, setIntercoms] = useState([]);
  const navigate = useNavigate();
  // Unlock/Talk + call state are shared with the IntercomView page via this hook.
  const { unlocking, unlockSuccess, handleUnlock, handleTalk, callState, activeIntercomId } = useIntercomControls();
  const audioRef = useRef(null);

  const { setWaveConfig } = useStore();

  // Reset on tenant change
  useEffect(() => {
    console.log('[Intercoms] Tenant changed, resetting data');
    setIntercoms([]);
    setLoading(true);
  }, [tenantVersion]);

  useEffect(() => {
    loadIntercoms();
    loadNotifications();
    
    const notificationInterval = setInterval(loadNotifications, 2000);
    const snapshotInterval = setInterval(() => {
      if (intercoms && intercoms.length > 0) {
        loadSnapshots();
      }
    }, 10000);
    
    return () => {
      clearInterval(notificationInterval);
      clearInterval(snapshotInterval);
    };
  }, [tenantVersion]);

  useEffect(() => {
    if (intercoms && intercoms.length > 0) {
      loadSnapshots();
    }
  }, [intercoms]);

  const loadIntercoms = async () => {
    try {
      const response = await fetchWithTenant('/api/tenant/config');
      const config = await response.json();

      // Merge legacy config intercoms with new integrations system
      let allIntercoms = []; // Legacy removed - all intercoms now in integrations

      try {
        const intResponse = await fetchWithTenant('/api/integrations');
        if (intResponse.ok) {
          const intData = await intResponse.json();
          const newIntercoms = (intData.integrations || [])
            .filter(i => i.categoryName === 'INTERCOM')
            .map(i => ({
              id: i.id,
              name: i.name || i.config?.name || 'Intercom',
              cameraId: i.config?.cameraId || '',
              n8nWebhookUrl: i.config?.n8nWebhookUrl || ''
            }));
          const existingIds = new Set(allIntercoms.map(i => i.id));
          allIntercoms = [...allIntercoms, ...newIntercoms.filter(i => !existingIds.has(i.id))];
        }
      } catch (e) {
        console.warn('[Intercoms] Could not load from integrations API:', e.message);
      }

      console.log('[Intercoms] Loaded intercoms:', allIntercoms);
      setIntercoms(allIntercoms);

      // Also set waveConfig for WebRTC player
      if (config.waveConfig) {
        setWaveConfig(config.waveConfig);
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load intercoms:', error);
      setLoading(false);
      setIntercoms([]);
    }
  };

  // Handle callId/intercomId from push notification
  useEffect(() => {
    const callId = searchParams.get("callId");
    const intercomIdParam = searchParams.get("intercomId");
    
    // Skip if no params or intercoms not loaded yet
    if (!callId && !intercomIdParam) return;
    if (intercoms.length === 0) {
      console.log('[Intercoms] Waiting for intercoms to load...');
      return;
    }
    
    console.log('[Intercoms] URL params - callId:', callId, 'intercomIdParam:', intercomIdParam);
    console.log('[Intercoms] Available intercoms:', intercoms.map(i => i.id));
    
    let targetIntercom = null;
    
    // First try to find by intercomId directly (most reliable)
    if (intercomIdParam) {
      targetIntercom = intercoms.find(i => i.id === intercomIdParam);
      console.log('[Intercoms] Found by intercomId:', targetIntercom ? targetIntercom.name : 'NOT FOUND');
    }
    
    // If not found, try via notification
    if (!targetIntercom && callId) {
      const notification = notifications.find(n => n.id === callId);
      if (notification) {
        targetIntercom = intercoms.find(i => i.id === notification.intercomId);
        console.log('[Intercoms] Found by notification:', targetIntercom ? targetIntercom.name : 'NOT FOUND');
      }
    }
    
    // Navigate to the intercom answer page
    if (targetIntercom) {
      console.log('[Intercoms] Opening intercom from push:', targetIntercom.name, 'cameraId:', targetIntercom.cameraId);
      // Clear URL params only after successfully resolving the target.
      setSearchParams({});
      navigate(`/intercom/${targetIntercom.id}`);
    } else if (intercoms.length > 0) {
      // Fallback - open the first intercom.
      setSearchParams({});
      navigate(`/intercom/${intercoms[0].id}`);
    }
  }, [searchParams, intercoms, notifications]);

  const loadNotifications = async () => {
    try {
      const response = await fetchWithTenant('/api/intercom/notifications');
      const data = await response.json();
      
      if (Array.isArray(data)) {
        if (data.length > notifications.length && notifications.length > 0) {
          playNotificationSound();
        }
        setNotifications(data);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const loadSnapshots = async () => {
    if (!intercoms || intercoms.length === 0) return;
    
    for (const intercom of intercoms) {
      if (intercom.cameraId) {
        try {
          const response = await fetchWithTenant(`/api/vms/cameras/${intercom.cameraId}/thumbnail`);
          if (response.ok) {
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setIntercomSnapshots(prev => ({
              ...prev,
              [intercom.id]: url
            }));
          }
        } catch (error) {
          console.error(`Failed to load snapshot for ${intercom.name}:`, error);
        }
      }
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadIntercoms();
    await loadSnapshots();
    setRefreshing(false);
    toast.success('Intercoms refreshed');
  };

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(console.error);
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await fetch(`/api/intercom/notifications/${notificationId}`, {
        method: 'DELETE'
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const dismissAllNotifications = async () => {
    try {
      await fetchWithTenant('/api/intercom/notifications', { method: 'DELETE' });
      setNotifications([]);
      toast.success('All notifications dismissed');
    } catch (error) {
      console.error('Failed to dismiss notifications:', error);
    }
  };

  const openIntercomView = (intercom) => {
    navigate(`/intercom/${intercom.id}`);
  };

  const intercomCount = intercoms ? intercoms.length : 0;
  const notificationCount = notifications ? notifications.length : 0;
  
  // Check if we have URL params from push notification - don't show empty state while loading
  const hasPushParams = searchParams.get("callId") || searchParams.get("intercomId");

  if (intercomCount === 0 && !loading && !hasPushParams) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title"><Phone size={22} /> Intercoms</h1>
          <p className="page-subtitle">Manage door stations & access control</p>
        </div>
        
        <div className="card">
          <div className="empty-state">
            <Phone size={48} />
            <h3>No Intercoms Configured</h3>
            <p>Add intercoms in Admin Settings to get started.</p>
            <a href="/admin" className="btn btn-primary mt-4">
              <Settings size={16} />
              Go to Admin Settings
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="md-page">
      <audio ref={audioRef} src="/notification.mp3" preload="auto" />
      <CcmLicenseBanner />

      <div className="page-header-with-actions">
        <div className="page-header-content">
          <h1 className="page-title"><Phone size={22} /> Intercoms</h1>
          <p className="page-subtitle">Video intercoms and door control</p>
        </div>
        <div className="page-header-actions">
          {notificationCount > 0 && (
            <button className="btn btn-secondary" onClick={dismissAllNotifications}>
              <X size={16} />
              Clear All
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
            Refresh
          </button>
          <span className="camera-count">{intercomCount} intercom{intercomCount !== 1 ? 's' : ''}</span>
          <button className="tutorial-help-btn" onClick={() => startTutorial(intercomsTutorial.module, intercomsTutorial.steps)} title="Take a tour" aria-label="Take a tour">
            <HelpCircle size={16} />
          </button>
        </div>
      </div>

      {/* Pending Notifications */}
      {notificationCount > 0 && (
        <div className="card notification-card">
          <h3 className="card-title">
            <Bell size={18} />
            Pending Notifications
          </h3>
          <div className="notification-list">
            {notifications.map(notification => (
              <div key={notification.id} className="notification-item">
                <div className="notification-thumbnail">
                  {notification.thumbnail ? (
                    <img src={notification.thumbnail} alt="Snapshot" />
                  ) : (
                    <div className="no-thumbnail">
                      <Camera size={24} />
                    </div>
                  )}
                </div>
                <div className="notification-info">
                  <span className="notification-name">{notification.intercomName}</span>
                  <span className="notification-time">
                    {new Date(notification.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="notification-event">{notification.eventType}</span>
                </div>
                <div className="notification-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleUnlock(notification.intercomId)}
                    disabled={loading}
                  >
                    <Unlock size={14} />
                    Unlock
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => dismissNotification(notification.id)}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Intercom Grid */}
      <div className="intercom-grid">
        {intercoms.map(intercom => (
          <div key={intercom.id} className="intercom-card-v2">
            {/* Image Area with Overlay */}
            <div className="intercom-card-image" onClick={() => openIntercomView(intercom)}>
              {/* Status Badge */}
              <div className="intercom-card-status">
                <span className={`status-dot ${intercom.cameraId ? 'online' : 'offline'}`} />
                {intercom.cameraId && <span className="status-text">LIVE</span>}
              </div>
              
              {/* Thumbnail */}
              {intercomSnapshots[intercom.id] ? (
                <img src={intercomSnapshots[intercom.id]} alt={intercom.name} />
              ) : (
                <div className="intercom-card-placeholder">
                  {intercom.cameraId ? (
                    <>
                      <div className="loading-spinner" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <Camera size={40} />
                      <span>No camera</span>
                    </>
                  )}
                </div>
              )}
              
              {/* Name Overlay */}
              <div className="intercom-card-overlay">
                <span className="intercom-card-name">{intercom.name}</span>
              </div>
            </div>
            
            {/* Action Bar */}
            <div className="intercom-card-actions">
              <button
                className="intercom-action-btn view"
                onClick={(e) => { e.stopPropagation(); openIntercomView(intercom); }}
              >
                <Video size={18} />
                <span>View</span>
              </button>
              <button
                className="intercom-action-btn unlock"
                onClick={(e) => { e.stopPropagation(); handleUnlock(intercom.id); }}
                disabled={loading || !intercom.n8nWebhookUrl}
              >
                <Unlock size={18} />
                <span>Unlock</span>
              </button>
              <button
                className={`intercom-action-btn talk ${callState === 'active' && activeIntercomId === intercom.id ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleTalk(intercom); }}
                disabled={!intercom.cameraId || (callState === 'active' && activeIntercomId !== intercom.id)}
              >
                {callState === 'active' && activeIntercomId === intercom.id ? <MicOff size={18} /> : <Mic size={18} />}
                <span>{callState === 'active' && activeIntercomId === intercom.id ? 'End' : 'Talk'}</span>
              </button>
            </div>
            
            {/* Warning if no webhook */}
            {!intercom.n8nWebhookUrl && (
              <div className="intercom-card-warning">
                <AlertCircle size={14} />
                <span>No webhook configured</span>
              </div>
            )}
          </div>
        ))}
      </div>


      {showPrompt && (
        <TutorialPrompt
          module={intercomsTutorial.module}
          moduleName={intercomsTutorial.moduleName}
          steps={intercomsTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default Intercoms;
